// Only BLE (0 <-> 'bt') and WiFi (1 <-> 'wifi') round-trip, address <-> mac; SRD and NFC have no V4 form.
// V4 beacons have no site_id: the floor plan is derived from the coordinate's altitude and footprint.
export const V4BeaconType = {BLE: 0, WiFi: 1};

// Display only, never sent: V4 stores no radius, 0 would draw an invisible circle, 5 m is the V3 minimum.
const DEFAULT_RADIUS = 5;

const num = value => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
};

export const v4BeaconTypeToV3 = type => (+type === V4BeaconType.WiFi ? 'wifi' : 'bt');

export const v3BeaconTypeToV4 = type =>
    (type === 'wifi' ? V4BeaconType.WiFi : V4BeaconType.BLE);

export const isV4BeaconType = type => type === 'bt' || type === 'wifi';

const siteCorners = site => {
    const get = key => (site?.get ? site.get(key) : site?.[key]);
    const tl = get('topLeft');
    const tr = get('topRight');
    const bl = get('bottomLeft');
    if ([tl, tr, bl].some(c => !Array.isArray(c) || c.length < 2)) return null;
    const p = c => [num(c[0]), num(c[1])];
    const [a, b, d] = [p(tl), p(tr), p(bl)];
    // The fourth corner closes the parallelogram; order matters for the ray cast.
    return [a, b, [b[0] + d[0] - a[0], b[1] + d[1] - a[1]], d];
};

function isInsidePolygon([lat, lng], polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [latI, lngI] = polygon[i];
        const [latJ, lngJ] = polygon[j];
        const intersects =
            ((lngI > lng) !== (lngJ > lng)) &&
            (lat < ((latJ - latI) * (lng - lngI)) / (lngJ - lngI) + latI);
        if (intersects) inside = !inside;
    }
    return inside;
}

// Footprint only, altitude ignored.
export function isInsideSiteFootprint(coordinate, site) {
    const latitude = num(coordinate?.latitude);
    const longitude = num(coordinate?.longitude);
    if (!latitude && !longitude) return false;
    const corners = siteCorners(site);
    return !!corners && isInsidePolygon([latitude, longitude], corners);
}

// `sites` must already be narrowed to the beacon's account (V3/V4 account ids differ, see siteScopeFor).
export function resolveSiteIdForCoordinate(coordinate, sites) {
    const latitude = num(coordinate?.latitude);
    const longitude = num(coordinate?.longitude);
    // 0,0 means unplaced.
    if (!latitude && !longitude) return null;

    const altitude = Math.round(num(coordinate?.altitude));
    const list = sites?.toArray ? sites.toArray() : (sites || []);
    const field = (s, key) => (s?.get ? s.get(key) : s?.[key]);
    const candidates = list.filter(
        s => s && Math.round(num(field(s, 'altitude'))) === altitude,
    );
    if (!candidates.length) return null;

    const inside = candidates.find(s => isInsideSiteFootprint({latitude, longitude}, s));
    if (inside) return inside.id;
    // Altitude 0 is the Record default, so it needs the footprint; a non-zero floor may bind just outside.
    return altitude ? candidates[0].id : null;
}

export function mapV4BeaconToV3(v4Beacon, sites, accountName = '') {
    const ownerId = v4Beacon.owner_id || null;
    return {
        id: v4Beacon.id,
        uuid: v4Beacon.id,
        name: v4Beacon.name || '',
        key: '',
        mac: v4Beacon.address || '',
        latitude: num(v4Beacon.coordinate?.latitude),
        longitude: num(v4Beacon.coordinate?.longitude),
        radius: DEFAULT_RADIUS,
        shape: 'circle',
        points: [],
        type: v4BeaconTypeToV3(v4Beacon.type),
        subtype: '-',
        accountId: ownerId || 0,
        accountName,
        siteId: resolveSiteIdForCoordinate(v4Beacon.coordinate, sites),
        hidden: false,
        batteryLowSince: null,
        major: null,
        minor: null,
        source: 'v4',
        v4OwnerId: ownerId,
        v4Data: v4Beacon,
    };
}

export function v3BeaconToV4Body(beacon, sites) {
    const get = key => (beacon?.get ? beacon.get(key) : beacon?.[key]);
    const siteId = get('siteId');
    const list = sites?.toArray ? sites.toArray() : (sites || []);
    const site = siteId ? list.find(s => String(s.id) === String(siteId)) : null;
    const coordinate = {
        latitude: num(get('latitude')),
        longitude: num(get('longitude')),
    };
    // The site's altitude is the plan link: sent even when 0 (ground floor), omitted only off a plan.
    if (site) {
        coordinate.altitude = Math.round(num(site.get ? site.get('altitude') : site.altitude));
    }
    return {
        name: get('name') || '',
        type: v3BeaconTypeToV4(get('type')),
        address: get('mac') || '',
        coordinate,
    };
}
